import * as React from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import { Box, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import { colors } from 'styles/colors';
import { fonts } from 'styles/fonts';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import useGlobalStyles from 'styles/globalStyles';

type CustomDatePickerProps = {
  name: string;
  control: Control<any>;
  error?: FieldError;
  placeholder?: string;
  label?: string;
  required?: boolean;
  note?: string;
  floatingLabel?: string;
} & Omit<DatePickerProps<any>, 'value' | 'onChange' | 'slotProps' | 'open'>;

export default function CustomDatePicker({
  name,
  control,
  error,
  placeholder,
  label,
  required,
  note,
  sx,
  floatingLabel,
  ...rest
}: CustomDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const globalStyles = useGlobalStyles();

  return (
    <Box>
      {label && <CustomLabel
        label={label}
        note={note}
        required={required} />}
      <Box>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              {...rest}
              value={field.value ?? null}
              onChange={(v) => field.onChange(v ?? undefined)}
              format="DD MMM, YYYY"
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              disableOpenPicker
              enableAccessibleFieldDOMStructure={false}
              label={floatingLabel}
              slots={{
                textField: TextField,
              }}
              slotProps={{
                textField: {
                  label: floatingLabel,
                  InputLabelProps: {
                    shrink: true,
                    required,
                    className: globalStyles.floatingLabelText,
                  },
                  size: 'small',
                  fullWidth: true,
                  error: !!error,
                  helperText: error?.message,
                  placeholder,
                  inputRef,
                  onClick: () => setOpen(true),
                  onMouseDown: (e: React.MouseEvent) => {
                    e.preventDefault();
                    // Keep the field itself as the focused element so MUI restores
                    // focus here (not wherever focus last happened to be) when the
                    // calendar closes - otherwise the page can jump to whatever was
                    // previously focused (e.g. an "Add procedure" button far below).
                    inputRef.current?.focus({ preventScroll: true });
                    setOpen(true);
                  },
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                      cursor: 'pointer',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8.5px 14px',
                      fontSize: '15px',
                      fontFamily: fonts.neue,
                      fontWeight: 500,
                      color: colors.black,
                      // cursor: 'pointer',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.secondary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.secondary,
                    },
                    '& input::placeholder': {
                      fontSize: '14px',
                      opacity: 1,
                      fontFamily: fonts.neue,
                      fontWeight: 500,
                      color: colors.secondary,
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: 0,
                    },
                    ...sx,
                  },
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
}
